import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, makeStyles, Box, Dialog } from "@material-ui/core";

import { api } from "../../../services";
import { HOTEL_HEADERS } from "../constants";
import { ReUploadFilePopupContent, updateUSAState } from "../noSolar";
import { EXCEL_FILE_TYPE } from "../../../constants";
import { ConfirmStep } from "../noSolar/steps/ConfirmStep";
import { UploadDataStep } from "../noSolar/steps/UploadDataStep";
import { StepBar, Steps } from "../components/StepBar";
import { header } from "../../workspace/settings/sample";
import SettingHeader from "../../../components/SettingHeader";
import { saveNotification } from "../../../redux/notification/actions";
import { DownloadTeamplateStep } from "../noSolar/steps/DownloadTeamplateStep";
import { ReviewCalculationStep } from "./steps/ReviewCalculationStep";
import { Button, TextButton, UploadButton } from "../../../components/base";
import { GLOBAL_DRAFT_URL } from "../../aroundtown/PortfolioRegistrationPage";

const HOTELS_TEMPLATE_FILE = "n_portfolio_registration_template.xlsx";
const HOTELS_DRAFT_URL = "/onboarding/n/portfolio-registration/draft/";

export const useStyles = makeStyles((theme) => ({
  popupContent: {
    padding: `${theme.spacing(4)}px ${theme.spacing(1)}px`,
    lineHeight: "2.5rem",
  },
  approveBtn: {
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
    marginLeft: theme.spacing(5),
  },
}));

export const HotelsPortfolioRegistrationPage = () => {
  const dispatch = useDispatch();
  const [rows, setRows] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadingData, setUploadingData] = useState(false);
  const [draft, setDraft] = useState();
  const [selectedFile, setSelectedFile] = useState();
  const [popup, setPopup] = useState();
  const [isDataConfirmed, setIsDataConfirmed] = useState(false);
  const { user } = useSelector((state) => state.auth);
  const [step, setStep] = useState(
    localStorage.getItem(`step-${user.cluster_registration_flow}-uid${user.id}`) !== null
      ? +localStorage.getItem(`step-${user.cluster_registration_flow}-uid${user.id}`)
      : Steps.downloadTemplate
  );
  const StepComponent = [DownloadTeamplateStep, UploadDataStep, ConfirmStep, ReviewCalculationStep][
    step
  ];

  const rowsRef = useRef();
  rowsRef.current = rows;

  useEffect(() => {
    api
      .get(GLOBAL_DRAFT_URL, { params: { clusters: user.clusters[0].id } })
      .then((resp) => resp.data)
      .then((draft) => {
        setDraft(draft);
        if (draft.buildings.length) {
          setRows(draft.buildings);
        }
      });
  }, []);

  const onNextStep = () => {
    if (step === Steps.downloadTemplate) {
      setStep(step + 1);
    } else if (step === Steps.updateData) {
      uploadFile();
    } else if (step === Steps.confirm) {
      setIsDataConfirmed(true);
      saveChanges();
    } else {
      setPopup(true);
    }
  };

  const onApprove = () => {
    setStep(Steps.updateData);
    setSelectedFile();
    setIsDataConfirmed(false);
    setPopup();
    localStorage.setItem(`step-${user.cluster_registration_flow}-uid${user.id}`, Steps.updateData);
  };

  const onBackStep = () => {
    if (step === Steps.reviewCalculation) {
      setIsDataConfirmed(false);
    }
    if (step > 0) {
      setStep(step - 1);
    }
  };

  const uploadFile = async (file = null) => {
    if (uploading) return;
    const form = new FormData();
    form.append("file", file || selectedFile);
    setUploading(true);
    return await api
      .post(`${HOTELS_DRAFT_URL}upload/`, form, {
        headers: { "Content-Type": "multipart/form-data" },
        params: { clusters: user.clusters[0].id },
      })
      .then((res) => res.data)
      .then((data) => {
        localStorage.setItem(`step-${user.cluster_registration_flow}-uid${user.id}`, Steps.confirm);
        setStep(Steps.confirm);
        setRows(updateUSAState(data.buildings));
        setDraft({
          ...draft,
          files: [
            ...(draft?.files || []),
            {
              id: data.id,
              name: data.name,
              ext: data.ext,
              size: data.size,
              file: data.file,
              created: data.created,
            },
          ],
        });
      })
      .catch((err) => {
        const statusCode = err?.response?.status;
        let message;
        if (statusCode === 400) {
          if ("file" in err.response.data) {
            message = err.response.data["file"][0];
          } else if ("error" in err.response.data) {
            message = err.response.data["error"];
          }
        } else if (statusCode === 500) {
          message =
            "Error when processing the uploaded file. Make sure the uploaded file matched with template";
        } else {
          message = "Something went wrong. Please try again";
        }
        dispatch(saveNotification({ message, error: true }));
      })
      .finally(() => {
        setUploading(false);
      });
  };

  const saveChanges = () => {
    if (uploadingData) return;
    setUploadingData(true);
    api
      .post(`${HOTELS_DRAFT_URL}save/`, rowsRef.current, {
        params: { clusters: user.clusters[0].id },
      })
      .then((resp) => resp.data)
      .then((draft) => {
        setRows(draft.buildings);
      })
      .catch((e) => {
        let message = "Oops...Something went wrong.";
        if (e.response && "error" in e.response.data) {
          message = e.response.data.error;
        } else if (e.response.data instanceof Array) {
          message = "Some fields are required";
        }
        dispatch(
          saveNotification({
            message: message,
            error: true,
          })
        );
      })
      .finally(() => {
        setUploadingData(false);
      });
  };

  const deleteBuilding = (id) => {
    api
      .delete(`${GLOBAL_DRAFT_URL}buildings/${id}/`, {
        params: { clusters: user.clusters[0].id },
      })
      .catch(() => {
        dispatch(
          saveNotification({
            message: "Oops...Something went wrong.",
            error: true,
          })
        );
      });
  };

  return (
    <Box display="flex" flexDirection="column" mx={3} justifyContent="space-between" height="100%">
      <Grid container direction="column" style={{ flex: 1 }}>
        <Box my={4}>
          <SettingHeader
            style={{ width: "fit-content" }}
            title={header.portfolioregistration.title}
            subtitle={header.portfolioregistration.subtitle}
            img={header.portfolioregistration.img}
          />
        </Box>
        <StepBar currentStep={step} isCurrentStepDone={step === Steps.confirm && isDataConfirmed} />
        <Box flex={1} overflowy="auto" pt={6} px={8} width="100%" boxSizing="border-box">
          <StepComponent
            selectedFile={selectedFile}
            setSelectedFile={setSelectedFile}
            rows={rows}
            setRows={setRows}
            disableTable={!draft?.is_table_enabled}
            deleteBuilding={deleteBuilding}
            isDataConfirmed={isDataConfirmed}
            setStep={setStep}
            templateFile={HOTELS_TEMPLATE_FILE}
            headers={HOTEL_HEADERS}
            draftUrl={HOTELS_DRAFT_URL}
          />
        </Box>
      </Grid>

      {(!isDataConfirmed || step > Steps.confirm) && (
        <Box mt={3.25}>
          <Grid container justifyContent="center" style={{ columnGap: 48 }}>
            {step !== Steps.downloadTemplate && <TextButton text="BACK" onClick={onBackStep} />}

            <Button
              text={
                step === Steps.confirm
                  ? "DATA IS CONFIRMED"
                  : step === Steps.reviewCalculation
                  ? "RE-UPLOAD DATA"
                  : "NEXT"
              }
              onClick={onNextStep}
              disabled={step === Steps.updateData && !selectedFile}
              loading={(step === Steps.updateData && uploading) || uploadingData}
            />

            {step === Steps.confirm && (
              <UploadButton
                text="RE-UPLOAD DATA"
                loading={uploading}
                onChange={(e) => uploadFile(e.target.files[0])}
                containerStyle={{ margin: 0 }}
                variant="outlined"
                accept={EXCEL_FILE_TYPE}
              />
            )}
          </Grid>
        </Box>
      )}

      <Dialog open={!!popup} maxWidth="sm">
        <Box mx={5} my={4}>
          <ReUploadFilePopupContent setPopup={setPopup} loading={uploading} onApprove={onApprove} />
        </Box>
      </Dialog>
    </Box>
  );
};
